import { useCallback, useEffect, useImperativeHandle, useRef, useState, type Ref } from 'react';

import { token } from '@bp-files/ui';
import {
  drawnStrokes,
  smoothed,
  strokesToInk,
  type RawPoint,
  type SignatureInk,
} from './signatureInk';
import { SIGNATURE_COPY } from './signature.copy';

/**
 * The drawing surface (USR-13).
 *
 * **Hand-rolled rather than `signature_pad`**, on the reasoning that keeps
 * `lucide-react` out of this workspace and left `ImageViewer` without a zoom
 * library: one caller, a small surface, and Article 2 makes a dependency with
 * lock-in an ADR. The library's value is its velocity-weighted stroke, and that
 * is thirty lines in `signatureInk.ts` — where, unlike inside a library, it is
 * unit-tested and produces the stroke *vectors* this feature stores rather than
 * a raster it would have to be reverse-engineered from. ADR-0055 records it.
 *
 * **This component owns pointer events and pixels, and no decisions.** What the
 * mark *is* — smoothing, simplification, the unit box — is `signatureInk.ts`,
 * because jsdom gives a canvas no 2D context and anything decided in here would
 * be untestable. The split is `ZoomStage`/`zoomMath`'s, one surface over.
 *
 * **Ink is captured in CSS pixels and the canvas is drawn in device pixels.**
 * The two are kept apart deliberately: normalisation divides the drawing out of
 * the mark entirely, so the display density must not reach the stored path.
 *
 * **The pad never moves what has been drawn.** It paints `drawnStrokes` — the
 * gesture in the coordinates it arrived in — and leaves trimming, aspect
 * padding and centring to `strokesToInk` at save. It used to paint the *stored*
 * form instead, on the reasoning that a preview richer than the record is a
 * preview of the wrong thing, and that reasoning cost more than it bought: the
 * stored form has the pad divided out of it, so the first touch was a
 * one-point box scaled to fill the pad and drawn dead centre, and the mark
 * rescaled and re-centred on every pointermove. Somebody signing on the left of
 * the pad watched their ink appear in the middle and corrected for it. What is
 * previewed here is now the *gesture*; what the record does to it is shown by
 * `StoredSignature` afterwards, which is the honest place for it.
 */

/** The pad's aspect ratio. Wide, because a signature is. */
const PAD_ASPECT = 3;

/**
 * Keep the pointer even if it leaves the canvas mid-stroke — and survive not
 * being able to.
 *
 * **jsdom does not implement pointer capture**, so an unguarded call takes the
 * whole component down in exactly the tests that exercise drawing, which are
 * the only ones worth having here. It can also throw in a real browser, for a
 * pointer that has already gone. Capture is an improvement to the gesture
 * rather than a requirement of it: without it a stroke ends at the pad's edge,
 * which is the same thing `onPointerLeave` already does deliberately.
 */
function capturePointer(element: Element, pointerId: number): void {
  try {
    element.setPointerCapture(pointerId);
  } catch {
    // Nothing to do, and nothing lost — see above.
  }
}

/** Never draw more than two canvas pixels per CSS pixel. `pdfPageLayout`'s cap. */
const MAX_DEVICE_PIXEL_RATIO = 2;

export interface SignaturePadHandle {
  clear: () => void;
  /** The mark as it would be stored, or `undefined` when there is nothing worth storing. */
  ink: () => SignatureInk | undefined;
}

export interface SignaturePadProps {
  /** Reports whether there is ink, so the caller can enable its save. */
  onInkChange: (hasInk: boolean) => void;
  ref?: Ref<SignaturePadHandle>;
}

export function SignaturePad({ onInkChange, ref }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokes = useRef<RawPoint[][]>([]);
  const drawing = useRef(false);
  /** The pad's CSS width as of the last resize, so the next one has a ratio. */
  const paddedWidth = useRef<number | undefined>(undefined);
  const [hasInk, setHasInk] = useState(false);

  /**
   * Repaint from the captured points.
   *
   * Everything is redrawn rather than appended to, because the width of a point
   * depends on the one after it — `signatureInk` smooths each width against its
   * neighbours — so the last segment is not final until the next arrives. A pad
   * that appended would show a stroke subtly different from the one it saved.
   */
  const repaint = useCallback(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    // jsdom has no 2D context at all, which is why nothing here decides
    // anything — see the header.
    if (!canvas || !context) return;

    const ratio = Math.min(window.devicePixelRatio || 1, MAX_DEVICE_PIXEL_RATIO);
    const cssWidth = canvas.clientWidth;
    const cssHeight = canvas.clientHeight;

    if (canvas.width !== Math.floor(cssWidth * ratio)) {
      canvas.width = Math.floor(cssWidth * ratio);
      canvas.height = Math.floor(cssHeight * ratio);
    }

    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, cssWidth, cssHeight);

    // No fit, no offset, no round trip through the stored form — the points
    // are already in this canvas's own coordinates, which is the whole reason
    // the ink lands under the pen. See the header.
    context.strokeStyle = token('bp-ink');
    context.lineCap = 'round';
    context.lineJoin = 'round';

    for (const stroke of smoothed(drawnStrokes(strokes.current))) {
      for (let i = 1; i < stroke.length; i += 1) {
        const from = stroke[i - 1];
        const to = stroke[i];
        if (from === undefined || to === undefined) continue;

        context.beginPath();
        context.lineWidth = (from.w + to.w) / 2;
        context.moveTo(from.x, from.y);
        context.lineTo(to.x, to.y);
        context.stroke();
      }

      // A single tap has no segment to draw, and a dot is what a person expects
      // to see when they touch the pad.
      const only = stroke[0];
      if (stroke.length === 1 && only !== undefined) {
        context.beginPath();
        context.arc(only.x, only.y, only.w / 2, 0, Math.PI * 2);
        context.fillStyle = token('bp-ink');
        context.fill();
      }
    }
  }, []);

  const reportInk = useCallback(() => {
    const present = strokesToInk(strokes.current) !== undefined;
    setHasInk(present);
    onInkChange(present);
  }, [onInkChange]);

  useImperativeHandle(
    ref,
    () => ({
      clear() {
        strokes.current = [];
        repaint();
        reportInk();
      },
      ink: () => strokesToInk(strokes.current),
    }),
    [repaint, reportInk],
  );

  /**
   * Keep the mark on the pad when the pad changes size.
   *
   * The fit used to make this free — it re-derived the drawing from the stored
   * form every frame, so a resize simply re-fitted. Painting the raw gesture
   * means the points are in the pixels they were captured in, and a phone
   * turned to portrait mid-signature would leave half the mark outside a
   * narrower pad. Scaling them by the width's own ratio is the same thing the
   * fit was doing, applied once, when the reason for it actually occurs. The
   * pad's aspect is fixed, so one factor covers both axes.
   */
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const observer = new ResizeObserver(() => {
      const next = canvas.clientWidth;
      const previous = paddedWidth.current;
      paddedWidth.current = next;

      if (previous !== undefined && previous > 0 && next > 0 && next !== previous) {
        const factor = next / previous;
        strokes.current = strokes.current.map((stroke) =>
          stroke.map((point) => ({ ...point, x: point.x * factor, y: point.y * factor })),
        );
      }

      repaint();
    });

    observer.observe(canvas);
    return () => {
      observer.disconnect();
    };
  }, [repaint]);

  function pointFrom(event: React.PointerEvent<HTMLCanvasElement>): RawPoint {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
      // `timeStamp` rather than a clock: it is the event's own, so a frame the
      // main thread was busy for does not read as a slow pen stroke.
      t: event.timeStamp,
    };
  }

  return (
    <div className="flex flex-col gap-2">
      <canvas
        ref={canvasRef}
        data-testid="signature-pad"
        role="img"
        aria-label={hasInk ? SIGNATURE_COPY.pad.drawn : SIGNATURE_COPY.pad.empty}
        // `touch-none` is what lets a finger draw instead of scrolling the page.
        className="w-full touch-none rounded-md border"
        style={{
          aspectRatio: PAD_ASPECT,
          borderColor: token('bp-border-strong'),
          background: token('bp-paper'),
          cursor: 'crosshair',
        }}
        onPointerDown={(event) => {
          capturePointer(event.currentTarget, event.pointerId);
          drawing.current = true;
          strokes.current = [...strokes.current, [pointFrom(event)]];
          repaint();
        }}
        onPointerMove={(event) => {
          if (!drawing.current) return;

          const current = strokes.current[strokes.current.length - 1];
          if (current === undefined) return;
          current.push(pointFrom(event));
          repaint();
        }}
        onPointerUp={() => {
          drawing.current = false;
          reportInk();
        }}
        onPointerCancel={() => {
          drawing.current = false;
          reportInk();
        }}
        onPointerLeave={() => {
          // A pen that leaves the pad mid-stroke ends it, rather than joining
          // wherever it comes back — which would draw a line across the mark.
          if (!drawing.current) return;
          drawing.current = false;
          reportInk();
        }}
      />

      <div className="flex items-center justify-between gap-3">
        <p className="text-xs" style={{ color: token('bp-text-secondary') }}>
          {SIGNATURE_COPY.pad.hint}
        </p>
        <button
          type="button"
          onClick={() => {
            strokes.current = [];
            repaint();
            reportInk();
          }}
          disabled={!hasInk}
          className="rounded-md border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-elevated disabled:cursor-not-allowed disabled:opacity-50"
          style={{ borderColor: token('bp-border-strong') }}
        >
          {SIGNATURE_COPY.pad.clear}
        </button>
      </div>
    </div>
  );
}
